import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import AthleteAvatar from './AthleteAvatar';
import GuardianRequests from './GuardianRequests';
import { guardianshipsService, type HeldWard } from '../../services/guardianships';

/**
 * "My athletes" on the settings page: who this account holds, and the way to
 * the page where they are managed (`/profile/athletes`).
 *
 * A managed account has no profile of its own to visit — that is the point of
 * it — so this is the only door to what exists in a parent's name. It is
 * always drawn, where it used to hide itself when empty: with nothing here a
 * parent would have nowhere to add their first child, which is half of what
 * the page is for. The names are a preview; adding, correcting and removing
 * are the page's.
 */
export default function MyAthletesCard() {
  const { t } = useTranslation();
  const [wards, setWards] = useState<HeldWard[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setWards(await guardianshipsService.wards());
    } catch {
      // Nothing to show rather than an error on a page about something else.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Card data-testid="my-athletes">
      <CardHeader>
        <CardTitle>{t('guardian.athletes.title')}</CardTitle>
        <CardDescription>{t('guardian.athletes.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <GuardianRequests onAnswered={load} />

        {!loading && wards.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('guardian.athletes.empty')}</p>
        )}

        {wards.length > 0 && (
          <ul className="space-y-2">
            {wards.map((ward) => (
              <li key={ward.id} className="flex items-center gap-2" data-testid="held-ward">
                <AthleteAvatar
                  name={ward.name}
                  surname={ward.surname}
                  color={ward.avatar_color}
                  size="sm"
                />
                <span className="truncate text-sm font-medium">{ward.full_name}</span>
              </li>
            ))}
          </ul>
        )}

        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/profile/athletes" data-testid="manage-athletes">
            {t('guardian.athletes.manage')}
            <ChevronRight className="size-4" aria-hidden />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
